import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { IoCheckmarkSharp, IoCloseSharp, IoPersonAdd, IoArrowUndo, IoChatbubbleEllipses, IoSearch, IoVolumeHigh, IoMusicalNotes, IoTvOutline, IoLogInOutline } from 'react-icons/io5';
import Avatar from '../components/Avatar';
import { getSuggestions, dismissSuggestion } from '../api/FriendsAPI';
import { useLanguage } from '../i18n/LanguageContext';
import { presenceLabelKey } from '../utils/presence';
import '../style/Profile.css';

const stop = (fn) => (ev) => { ev.stopPropagation(); fn(); };
const nameOf = (u) => u.fullName || u.username;

// "Tanıyor olabileceğin kişiler", fetched once per visit of the Friends page
// - so a card you just acted on stays put (its buttons follow the lists)
// instead of vanishing under your cursor. null while loading.
export function useSuggestions() {
  const [suggestions, setSuggestions] = useState(null);
  useEffect(() => {
    let alive = true;
    getSuggestions().catch(() => ({ status: 'fail' })).then((res) => {
      if (alive) setSuggestions(res.status === 'success' ? res.data : []);
    });
    return () => { alive = false; };
  }, []);
  const hide = useCallback((u) => {
    setSuggestions((list) => (list || []).filter((s) => s.username !== u.username));
    dismissSuggestion(u.username).catch(() => {});
  }, []);
  return [suggestions, hide];
}

// The suggestions still worth showing: a blocked person, or one who has
// since asked you (they're under requests), drops out.
export function visibleSuggestions(suggestions, lists) {
  const blocked = new Set(lists.blocked.map((u) => u.username));
  const incoming = new Set(lists.incoming.map((u) => u.username));
  return (suggestions || []).filter((s) => !blocked.has(s.username) && !incoming.has(s.username));
}

// Suggestion cards - a grid in the right pane, a sideways strip on phones.
// Where each one stands (sent, already a friend) comes from the page's
// lists, which are always fresh.
export function SuggestionCards({ items, lists, className, onSelect, onAdd, onCancel, onMessage, onHide }) {
  const { t } = useLanguage();
  const friends = useMemo(() => new Set(lists.friends.map((u) => u.username)), [lists.friends]);
  const outgoing = useMemo(() => new Set(lists.outgoing.map((u) => u.username)), [lists.outgoing]);

  const mutualText = (s) => [
    s.mutualFriendCount > 0 && t(s.mutualFriendCount === 1 ? 'people.mutualFriendsOne' : 'people.mutualFriends', { count: s.mutualFriendCount }),
    s.mutualGroupCount > 0 && t(s.mutualGroupCount === 1 ? 'people.mutualGroupsOne' : 'people.mutualGroups', { count: s.mutualGroupCount })
  ].filter(Boolean).join(' · ');

  const buttons = (s) => {
    if (friends.has(s.username)) {
      return <button type="button" className="ProfileActionBtn secondaryColor" onClick={stop(() => onMessage(s))}><IoChatbubbleEllipses /><span>{t('common.sendMessage')}</span></button>;
    }
    if (outgoing.has(s.username)) {
      return <button type="button" className="ProfileActionBtn secondaryColor" onClick={stop(() => onCancel(s))} title={t('profile.cancelRequest')}><IoArrowUndo /><span>{t('people.requestSent')}</span></button>;
    }
    return (
      <>
        <button type="button" className="ProfileActionBtn primaryColor" onClick={stop(() => onAdd(s))}><IoPersonAdd /><span>{t('profile.addFriend')}</span></button>
        <button type="button" className="ProfileActionBtn ProfileActionIcon secondaryColor" onClick={stop(() => onHide(s))} title={t('people.hide')} aria-label={t('people.hide')}><IoCloseSharp /><span>{t('people.hide')}</span></button>
      </>
    );
  };

  return (
    <ul className={className}>
      {items.map((s) => (
        <li key={s.username} className="PeopleSuggest glassCard" onClick={() => onSelect(s)}>
          <Avatar imageUrl={s.imageUrl} alt="" className="PeopleSuggestAvatar" />
          <b className="PeopleSuggestName">{nameOf(s)}</b>
          <span className="descGray PeopleSuggestHandle">@{s.username}</span>
          <span className="PeopleSuggestMutual">
            {s.mutualFriends.length > 0 && (
              <span className="PeopleMutualStack" aria-hidden="true">
                {s.mutualFriends.map((f) => <Avatar key={f.username} imageUrl={f.imageUrl} alt="" />)}
              </span>
            )}
            <span className="descGray">{mutualText(s)}</span>
          </span>
          <div className="PeopleSuggestActions">{buttons(s)}</div>
        </li>
      ))}
    </ul>
  );
}

// Phones: who's online, as a sideways strip of avatars above the list (the
// right pane, with its "Şu an aktif" cards, only opens once someone's picked).
export function ActiveStrip({ active, onSelect }) {
  const { t } = useLanguage();
  if (active.length === 0) return null;
  return (
    <div className="PeopleActiveStrip" aria-label={t('people.activeTitle', { count: active.length })}>
      {active.map((f) => (
        <button key={f.username} type="button" className="PeopleActiveStripItem" onClick={() => onSelect(f)}>
          <span className="PeopleActiveAvatar">
            <Avatar imageUrl={f.imageUrl} alt="" />
            <span className={`ProfileStatusDot ProfileStatus-${f.status}`} />
            {f.voice && <span className="PeopleActiveVoiceBadge"><IoVolumeHigh /></span>}
          </span>
          <span className="PeopleActiveStripName">{nameOf(f)}</span>
        </button>
      ))}
    </div>
  );
}

// The Friends page's right pane while nobody is selected: requests waiting
// on you, who's online and where, and people you may know.
export default function FriendsHome({ lists, loaded, active, suggestions, onHide, onSelect, onAccept, onIgnore, onAdd, onCancel, onMessage, onOpenGroup, onFindPeople }) {
  const { t } = useLanguage();
  const shown = visibleSuggestions(suggestions, lists);
  const name = nameOf;
  // A personal room is named after its owner, as on the Groups page.
  const roomLabel = (v) => (v.roomType === 'personal' && v.roomOwner
    ? t('people.personalRoom', { name: v.roomOwner.fullName || v.roomOwner.username })
    : v.roomName);

  const nothingYet = loaded && suggestions !== null && lists.friends.length === 0 && lists.incoming.length === 0 && shown.length === 0;

  return (
    <div className="PeoplePane PeopleHome glass">
      {lists.incoming.length > 0 && (
        <section className="PeopleHomeSection">
          <h4 className="PeopleHomeTitle">{t('people.incomingTitle', { count: lists.incoming.length })}</h4>
          <ul className="PeopleRequestGrid">
            {lists.incoming.map((u) => (
              <li key={u.username} className="PeopleRequest glassCard" onClick={() => onSelect(u)}>
                <Avatar imageUrl={u.imageUrl} alt="" />
                <div className="PeopleRequestText">
                  <b>{name(u)}</b>
                  <span className="descGray">@{u.username}</span>
                </div>
                <button type="button" className="AddButton primaryColor" onClick={stop(() => onAccept(u))} title={t('friends.accept')}><IoCheckmarkSharp /></button>
                <button type="button" className="AddButton cancelColor" onClick={stop(() => onIgnore(u))} title={t('friends.ignore')}><IoCloseSharp /></button>
              </li>
            ))}
          </ul>
        </section>
      )}

      {lists.friends.length > 0 && (
        <section className="PeopleHomeSection">
          <h4 className="PeopleHomeTitle">{t('people.activeTitle', { count: active.length })}</h4>
          {active.length === 0 && <p className="descGray PeopleHomeEmpty">{t('friends.emptyOnline')}</p>}
          <ul className="PeopleActiveGrid">
            {active.map((f) => (
              <li key={f.username} className="PeopleActive glassCard" onClick={() => onSelect(f)}>
                <div className="PeopleActiveHead">
                  <span className="PeopleActiveAvatar">
                    <Avatar imageUrl={f.imageUrl} alt="" />
                    <span className={`ProfileStatusDot ProfileStatus-${f.status}`} />
                  </span>
                  <div className="PeopleRequestText">
                    <b>{name(f)}</b>
                    <span className="descGray">
                      {t(presenceLabelKey(f.status))}{f.voice ? ` · ${t('people.inVoice')}` : ''}
                    </span>
                  </div>
                  <button type="button" className="AddButton primaryColor" onClick={stop(() => onMessage(f))} title={t('common.sendMessage')}><IoChatbubbleEllipses /></button>
                </div>
                {f.voice && f.voice.shared && (
                  <div className="PeopleActiveVoice">
                    <div className="PeopleActiveLine"><IoVolumeHigh /><span>{f.voice.groupName} · {roomLabel(f.voice)}</span></div>
                    {f.voice.music && <div className="PeopleActiveLine"><IoMusicalNotes /><span>{f.voice.music}</span></div>}
                    {f.voice.video && <div className="PeopleActiveLine"><IoTvOutline /><span>{f.voice.video}</span></div>}
                    <button type="button" className="ProfileActionBtn secondaryColor" onClick={stop(() => onOpenGroup(f.voice))}>
                      <IoLogInOutline /><span>{t('people.openGroup')}</span>
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        </section>
      )}

      {shown.length > 0 && (
        <section className="PeopleHomeSection">
          <h4 className="PeopleHomeTitle">{t('people.suggestionsTitle')}</h4>
          <SuggestionCards items={shown} lists={lists} className="PeopleSuggestGrid"
            onSelect={onSelect} onAdd={onAdd} onCancel={onCancel} onMessage={onMessage} onHide={onHide} />
        </section>
      )}

      {nothingYet && (
        <div className="PeopleEmpty PeopleWelcome">
          <IoSearch className="PeopleEmptyIcon" />
          <h4>{t('people.welcomeTitle')}</h4>
          <p className="descGray">{t('people.welcomeBody')}</p>
          <button type="button" className="SendButton primaryColor" onClick={onFindPeople}>{t('people.findPeople')}</button>
        </div>
      )}
    </div>
  );
}
